import { fetchWithHeaders, stringUtils } from '@sendible/common';
import { Button, Input, Spinner, Text, useToastNotificationContext } from '@sendible/design-system/src';
import sharedStateBridge from '@sendible/shared-state-bridge';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NotificationTypeEnum } from '@sendible/design-system/src/components/molecules/Toast';
import { ModalComponent, Heading, InputContainer, InputHeader, PopoverContent, PasswordHelper, Container, Content, Footer } from './index.styles';
import { ProfilesUrls } from '../../constants/urls';

type BlueskyReconnectionProps = {
  accountId?: number | null;
  closeCallback?: () => void | null;
  getUrlWithCredentials: (url: string) => string;
  successCallback?: () => void | null;
};

const BlueskyConnection = ({ accountId = null, closeCallback, getUrlWithCredentials, successCallback }: BlueskyReconnectionProps) => {
  const isReconnection = !!accountId;
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [usernameError, setUsernameError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [isLoading, setIsLoading] = useState(isReconnection);
  const [isSaving, setIsSaving] = useState(false);
  const { showToastNotification } = useToastNotificationContext();

  const { t } = useTranslation('bluesky');

  useEffect(() => {
    if (isReconnection) {
      const url = `${getUrlWithCredentials(ProfilesUrls.account)}&account_id=${accountId}`;

      const getAccount = async () => {
        const { result } = await fetchWithHeaders({
          method: 'GET',
          url,
        });

        if (result?.username) {
          setUsername(`@${result.username}`);
        }

        setIsLoading(false);
      };

      getAccount();
    }
  }, []);

  const showErrorMessage = () => {
    const notificationText = isReconnection
      ? `${t('cant_reconnect_your_account')} (${username}). ${t('try_again')}.`
      : `${t('cant_connect_your_account')} (${username}). ${t('try_again')}.`;

    showToastNotification({
      hideTimeout: 10,
      notificationText,
      type: NotificationTypeEnum.Error,
      showCloseButton: true,
    });
  };

  const showSuccessMessage = () => {
    const notificationText = isReconnection
      ? `${t('account_connected')} (${username}) ${t('has_been_reconnected')}.`
      : `${t('account_connected')} (${username}) ${t('has_been_connected')}.`;

    showToastNotification({
      hideTimeout: 10,
      notificationText,
      type: NotificationTypeEnum.Success,
      showCloseButton: true,
    });
  };

  const handleConnect = async () => {
    const sanitisedUsername = username.replace(/@/g, '');

    setIsSaving(true);

    try {
      const url = `${getUrlWithCredentials(ProfilesUrls.blueskyAuthenticate)}&blueskyUsername=${sanitisedUsername}&blueskyPassword=${password}${
        isReconnection ? `&account_id=${accountId}` : ''
      }`;

      const { result } = await fetchWithHeaders({
        method: 'POST',
        url,
        headers: { 'Content-Type': 'application/json' },
      });

      if (result?.status && result.status === 'Succeeded') {
        showSuccessMessage();

        if (window?.Backbone) {
          window.Backbone.trigger('bluesky-connected');
        }

        sharedStateBridge.updateSpecificKey('app', 'blueskyConnectionModal', {
          isConnectOpen: false,
          reconnectionAccountId: null,
        });

        if (successCallback) {
          successCallback();
        }
      } else {
        showErrorMessage();
      }
    } catch (err) {
      showErrorMessage();
    } finally {
      setIsSaving(false);
    }
  };

  const isUsernameValid = () => {
    const usernameMatch = username.match(/(\w*)\.?/);
    let usernameCorrectLength = true;

    if (usernameMatch && usernameMatch[0] && usernameMatch[0].length > 19) {
      usernameCorrectLength = false;
    }

    return username.trim() !== '' && username.includes('.') && usernameCorrectLength;
  };
  const isPasswordValid = () => password.trim().length > 18;
  const isFormValid = isUsernameValid() && isPasswordValid();

  const blueSkyHandlePopover = (
    <PopoverContent>
      <ol>
        <li>
          <span>
            {t('open_bluesky_app')}{' '}
            <a
              href="https://bsky.app/"
              target="_blank"
              rel="noreferrer"
            >
              {t('bluesky_app')}
            </a>{' '}
            {t('and_login')}
          </span>
          {t('navigate_to_profile')}
        </li>
        <li>
          <span>{t('find_username')}</span>
          {t('go_to_profile_instruction')}
        </li>
      </ol>
    </PopoverContent>
  );

  const blueSkyAppPasswordPopover = (
    <PopoverContent>
      <ol>
        <li>
          <span>
            {t('go_to')}{' '}
            <a
              href="https://bsky.app/settings/app-passwords"
              target="_blank"
              rel="noreferrer"
            >
              {t('bluesky_settings_path')}
            </a>
          </span>
        </li>
        <li>
          <span>{t('generate_new_password')}</span>
          {t('add_app_password_instruction')}
        </li>
        <li>
          <span>{t('paste_in_field')}</span>
          {t('use_generated_password')}
        </li>
      </ol>
      <hr />
      <p>
        {t('app_passwords_management')}{' '}
        <a
          href="https://bsky.app/settings/app-passwords"
          target="_blank"
          rel="noreferrer"
        >
          {t('bluesky_settings')}
        </a>
        .
      </p>
    </PopoverContent>
  );

  return (
    <ModalComponent
      variation="medium"
      headingTitle={
        <Heading>
          <img
            src="/s3_assets/icons/flat/v2/composebox/bluesky.svg"
            width="24"
            height="24"
            alt="Bluesky logo"
          />{' '}
          {isReconnection ? t('bluesky_reconnect') : t('bluesky_connect')}
        </Heading>
      }
      close={() => {
        sharedStateBridge.updateSpecificKey('app', 'blueskyConnectionModal', {
          isConnectOpen: false,
          reconnectionAccountId: null,
        });

        if (closeCallback) {
          closeCallback();
        }
      }}
    >
      {isLoading ? (
        <Spinner size="lg" />
      ) : (
        <Container>
          <Content>
            <InputContainer>
              <InputHeader>{t('bluesky_handle')}</InputHeader>
              <Input
                ariaLabel={t('bluesky_handle') as string}
                change={(value) => {
                  const sanitised = stringUtils.removeControlCharacters(value);

                  setUsername(sanitised);
                  setUsernameError(false);
                }}
                label={t('bluesky_username_placeholder') as string}
                popOver={{
                  label: t('where_is_this') as string,
                  content: blueSkyHandlePopover,
                }}
                value={username}
                blur={() => {
                  if (!isUsernameValid()) {
                    setUsernameError(true);
                  }
                }}
                size="md"
                feedback={
                  usernameError
                    ? {
                        type: 'danger',
                        message: t('username_error') as string,
                      }
                    : undefined
                }
              />
            </InputContainer>
            <InputContainer>
              <InputHeader>{t('enter_bluesky_password')}</InputHeader>
              <Input
                ariaLabel={t('bluesky_password') as string}
                className="bluesky-input"
                change={(value) => {
                  const cleaned = value.replace(/[^a-zA-Z0-9]/g, '');
                  const limited = cleaned.slice(0, 16);
                  const formatted = limited.replace(/(.{4})/g, '$1-');
                  const sanitised = stringUtils.removeControlCharacters(formatted);

                  setPassword(sanitised.replace(/-$/, ''));
                  setPasswordError(false);
                }}
                maxLength={19}
                label="____-____-____-____"
                value={password}
                blur={() => {
                  if (!isPasswordValid()) {
                    setPasswordError(true);
                  }
                }}
                popOver={{
                  label: t('where_is_this') as string,
                  content: blueSkyAppPasswordPopover,
                }}
                feedback={
                  passwordError
                    ? {
                        type: 'danger',
                        message: t('password_error') as string,
                      }
                    : undefined
                }
              />
              <PasswordHelper>
                {t('not_your_account_password')}
                &nbsp;
                <a
                  href="https://bsky.app/settings/app-passwords"
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('bluesky_settings')}
                </a>
                .
              </PasswordHelper>
            </InputContainer>
          </Content>
          <Footer>
            <Text>{t('pds_not_supported')}</Text>
            <Button
              appearance="primary"
              disabled={!isFormValid || isSaving}
              isLoading={isSaving}
              label={t('connect') as string}
              onClick={handleConnect}
              size={14}
              variation="fill"
            />
          </Footer>
        </Container>
      )}
    </ModalComponent>
  );
};

export default BlueskyConnection;
